import {getNodeImage} from "../components/NetworkGraph/utils.js";


const haveEdgeNodes = (edge, nodes) => {
  const haveSource = nodes.some(node => node.key === edge.source);
  const haveTarget = nodes.some(node => node.key === edge.target);
  return haveSource && haveTarget;
}

const getRandomPosition = () => Math.random()*10;

const genEdgeMapKey = (edge) => {
  return `${edge.source}-${edge.target}`;
}

const buildNode = (apiNode, nodeType, isOriginNode, nodeCoords) => {
  return {
    key: apiNode.key,
    x: nodeCoords ? nodeCoords.x + ((-1 + (Math.random() * 2)) / 4) : getRandomPosition(),
    y: nodeCoords ? nodeCoords.y + ((-1 * (Math.random() * 2)) / 4) : getRandomPosition(),
    // Setting the node image to unknown here due to what appears to be a bug with image
    // caching in the default sigma node renderer for the first image URL processed. The 
    // actual node image will be set correctly via the GraphDataController after the initial 
    // loading of data into the graph to avoid images not being shown for certain node types.
    image: getNodeImage("Unknown"),
    // image: getNodeImage(nodeType),
    label: apiNode.attributes.label_en || apiNode.attributes.label || "",
    cluster: nodeType,
    clusterLabel: nodeType,
    nodeType,
    isOriginNode,
    type: "image",
    zIndex: 1,
    attributes: {
      key: apiNode.key,
      tag: apiNode.attributes.entityType,
      entityType: nodeType,
      label: apiNode.attributes.label_en || apiNode.attributes.label || "",
    }
  }
}

const buildEdge = (apiEdge) => {
  const eType = apiEdge.attributes.type;
  const capitalizedType = eType.charAt(0).toUpperCase() + eType.toLowerCase().slice(1)
  const normalizedType = capitalizedType.replace(/_/g, " ")
  return {
    // key: apiEdge.key,
    key: genEdgeMapKey(apiEdge),
    source: apiEdge.source,
    target: apiEdge.target,
    attributes: {
      edgeType: normalizedType,
      label: normalizedType,
    }
  }
}

export const graphTransformer = (graphData, sourceNodeId) => {

  const clusters = new Set();
  const relationships = new Set();

  const nodeMap = {};
  let nodes = [];
  if (graphData.nodes) {
    const validNodes = graphData.nodes.filter((node) => {
      return node.key && node.key.length > 0;
    });
    const uniqueNodes = validNodes.reduce((acc, current) => {
      if (!acc.find((item) => item.key === current.key)) {
        acc.push(current);
      }
      return acc;
    }, []);
    nodes = uniqueNodes.map((node) => {
      const nodeType = node.attributes.entityType;
      if (nodeType) {
        clusters.add(nodeType);
      }
      const isOriginNode = node.key === sourceNodeId;
      const newNode = buildNode(node, nodeType, isOriginNode);
      nodeMap[node.key] = newNode;
      return newNode;
    })
  }

  const edgeMap = {}
  let edges = [];
  if (nodes.length > 0 && graphData.edges) {
    const validEdges = graphData.edges.filter((edge) => {
      return edge.key && edge.key.length > 0 && haveEdgeNodes(edge, nodes);
    });
    edges = validEdges.map((edge) => {
      edgeMap[genEdgeMapKey(edge)] = true;
      const builtEdge = buildEdge(edge);
      if (edge.attributes.type) {
        relationships.add(edge.attributes.type);
      }
      if ((edge.source === sourceNodeId) && (nodeMap[edge.source])) {
        nodeMap[edge.target].originEntityRelation = builtEdge.attributes.edgeType;
        nodeMap[edge.target].originEntityDirection = "target";
      }
      else if ((edge.target === sourceNodeId) && (nodeMap[edge.target])) {
        nodeMap[edge.source].originEntityRelation = builtEdge.attributes.edgeType;
        nodeMap[edge.source].originEntityDirection = "source";
      }
      return builtEdge
    })
  }

  return {
    nodeMap,
    edgeMap,
    graphData: {
      nodes,
      edges,
      attributes: { clusters: [...clusters], relationships: [...relationships] },
      options: { "type": "directed", "multi": false, "allowSelfLoops": true },
    }
  };
}


export const graphExtendTransformer = (graphData, edgeMap, nodeMap, extendData, nodeCoords) => {

  const clusters = new Set(graphData.attributes.clusters)
  const relationships = new Set(graphData.attributes.relationships);
  let numNodesAdded = 0;
  let numEdgesAdded = 0;

  const newNodeMap = {...nodeMap};
  let newNodes = [...graphData.nodes];
  if (extendData.nodes) {
    const validNodes = extendData.nodes.filter((node) => {
      return node.key && node.key.length > 0 && !nodeMap[node.key];
    });
    const uniqueNodes = validNodes.reduce((acc, current) => {
      if (!acc.find((item) => item.key === current.key)) {
        acc.push(current);
      }
      return acc;
    }, []);
    numNodesAdded = uniqueNodes.length;
    newNodes = newNodes.concat(uniqueNodes.map((node) => {
      newNodeMap[node.key] = true;
      const nodeType = node.attributes.entityType;
      if (nodeType) {
        clusters.add(nodeType);
      }
      return buildNode(node, nodeType, false, nodeCoords);
    }));
  }

  const newEdgeMap = {...edgeMap}
  let newEdges = [...graphData.edges];
  if (extendData.nodes.length > 0 && extendData.edges) {
    const validEdges = extendData.edges.filter((edge) => {
      return edge.key && edge.key.length > 0 
        && !edgeMap[genEdgeMapKey(edge)]
        && haveEdgeNodes(edge, extendData.nodes);
    });
    numEdgesAdded = validEdges.length;
    newEdges = newEdges.concat(validEdges.map((edge) => {
      newEdgeMap[genEdgeMapKey(edge)] = true;
      if (edge.attributes.type) {
        relationships.add(edge.attributes.type);
      }
      return buildEdge(edge);
    }));
  }

  return {
    nodeMap: newNodeMap,
    edgeMap: newEdgeMap,
    graphData: {
      ...graphData,
      nodes: newNodes,
      edges: newEdges,
      attributes: { clusters: [...clusters], relationships: [...relationships] },
    },
    hasChanges: (numNodesAdded > 0) || (numEdgesAdded > 0),
    numNodesAdded,
    numEdgesAdded, 
  };
}
